import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import styles from "./styles.module.scss";
import Modal from "../../../../../../components/common/Modal";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../../components/common/OutlinedButton";
import TextInput from "../../../../../../components/forms/textInput";
import { CheckoutLoginMode } from "../../../../../../setup/api/services/organization/models";

export const CHECKOUT_LOGIN_MODES: CheckoutLoginMode[] = [
  "any",
  "hint",
  "ticket",
];

interface ICheckoutLoginModePromptModalProps {
  open: boolean;
  currentValue: CheckoutLoginMode | undefined;
  handleClose: () => void;
  // eslint-disable-next-line no-unused-vars
  handleConfirm: (mode: CheckoutLoginMode, reason: string) => void;
}

/**
 * Collects the new checkout login mode and a required reason before changing it (partner API design
 * doc §7.3b): a stricter mode makes some of the org's checkout URLs stop working, so the change is
 * documented like every other client setting (auto-accept-consent, send limits).
 */
const CheckoutLoginModePromptModal = ({
  open,
  currentValue,
  handleClose,
  handleConfirm,
}: ICheckoutLoginModePromptModalProps) => {
  const { t } = useTranslation();
  const current: CheckoutLoginMode = currentValue ?? "any";
  const [mode, setMode] = useState<CheckoutLoginMode>(current);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setMode(current);
    } else {
      setReason("");
    }
  }, [open, current]);

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {t("lbl_edit_checkout_login_mode_title")}
        </h1>
        <p className={styles.subtitle}>
          {t("lbl_checkout_login_mode_subtitle")}
        </p>

        <RadioGroup
          value={mode}
          onChange={(_, value) => setMode(value as CheckoutLoginMode)}>
          {CHECKOUT_LOGIN_MODES.map((option) => (
            <div key={option} className={styles.option}>
              <FormControlLabel
                value={option}
                control={<Radio />}
                label={t(`lbl_checkout_login_mode_${option}`)}
              />
              <p className={styles.optionDescription}>
                {t(`lbl_checkout_login_mode_${option}_desc`)}
              </p>
            </div>
          ))}
        </RadioGroup>

        <TextInput
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("lbl_reason_prompt_placeholder")}
        />

        <div className={styles.actions}>
          <PrimaryButton
            disabled={!reason.trim() || mode === current}
            onClick={() => handleConfirm(mode, reason.trim())}>
            {t("lbl_confirm")}
          </PrimaryButton>
          <OutlinedButton onClick={handleClose}>
            {t("lbl_cancel")}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default CheckoutLoginModePromptModal;
